import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import Table from "../../core/pagination/datatable";
import Swal from "sweetalert2";
import {
  ChevronUp,
  PlusCircle,
} from "feather-icons-react/build/IconComponents";
import AddVariant from "../../core/modals/inventory/addvariant";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { setToogleHeader } from "../../core/redux/action";
import withReactContent from "sweetalert2-react-content";
import { useToast } from "../../Toaster/ToastContext";
import Loader from "../loader/loader";
import { apiClient } from "../../services/apiClient";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";

const VariantAttributes = () => {
  const dispatch = useDispatch();
  const data = useSelector((state) => state.toggle_header);
  const [variants, setVariants] = useState([]);
  const [filteredVariants, setFilteredVariants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentVariant, setCurrentVariant] = useState(null);
  const { showToast } = useToast();
  const hasFetched = useRef(false);
  const [permissions, setPermissions] = useState({
    create: false,
    read: false,
    update: false,
    delete: false,
  });

  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 20,
    totalPages: 0,
    totalCount: 0,
  });

  const renderCollapseTooltip = (props) => (
    <Tooltip id="refresh-tooltip" {...props}>
      Collapse
    </Tooltip>
  );

  useEffect(() => {
    const storedPermissions = localStorage.getItem("rolespermissions");
    let parsedPermissions = [];
    try {
      const Permissions = JSON.parse(storedPermissions);
      const getPermissions = JSON.parse(Permissions.permissions);
      if (getPermissions.permissions) {
        parsedPermissions = getPermissions.permissions;
      } else {
        parsedPermissions = getPermissions;
      }

      const inventoryPermissions = parsedPermissions.find(
        (item) => item.title === "Inventory"
      );

      const productPermissions = inventoryPermissions?.childPage?.find(
        (child) => child.title === "Variant Attributes"
      );
      // console.log("search product", productPermissions)

      if (productPermissions) {
        setPermissions({
          create: productPermissions.create || false,
          read: productPermissions.read || false,
          update: productPermissions.update || false,
          delete: productPermissions.delete || false,
        });
      } else {
        console.error("Product permissions not found in localStorage");
        showToast(
          "Product permissions not found.",
          "error",
          "Permissions Error"
        );
      }
    } catch (error) {
      console.error("Error parsing permissions from localStorage:", error);
      showToast("Failed to load permissions.", "error", "Permissions Error");
    }
  }, []);

  const fetchVariants = async (currentPage = 1, pageSize = 20) => {
    if (hasFetched.current) return;
    setLoading(true);
    const pageIndex = currentPage - 1;
    try {
      const response = await apiClient.post("/paginate-varient", {
        pageIndex,
        pageSize,
      });
      const data = await response;
      if (data.success) {
        setVariants(data.data);
        setFilteredVariants(data.data);
        setPagination({
          currentPage: data.currentPage ?? currentPage,
          pageSize: data.pageSize ?? pageSize,
          totalPages: data.totalPages ?? 1,
          totalCount: data.totalCount || data.data.length,
        });
      } else {
        setVariants([]);
        setFilteredVariants([]);
      }
    } catch (error) {
      showToast(
        error.message || "Failed to fetch categories",
        "error",
        "Categories"
      );
    } finally {
      setLoading(false);
      hasFetched.current = true;
    }
  };

  // useEffect(() => {
  //   fetchVariants();
  // }, []);
  useEffect(() => {
    if (!hasFetched.current) {
      fetchVariants(pagination.currentPage, pagination.pageSize);
    }
  }, [pagination.currentPage, pagination.pageSize]);

  useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      currentPage: 1,
    }));
    hasFetched.current = false;
  }, []);

  const handleAddVariants = () => {
    setCurrentVariant(null);
  };

  const handleEditVariants = (variant) => {
    setCurrentVariant(variant);
  };

  const handleSearch = (e) => {
    const term = e.target.value.toLowerCase();
    setFilteredVariants(
      variants.filter(
        (variant) =>
          variant.name.toLowerCase().includes(term) ||
          variant.value.toLowerCase().includes(term) ||
          variant.status.toLowerCase().includes(term)
      )
    );
  };

  const handleToggleStatus = async (variant) => {
    if (!permissions.update) {
      showToast(
        "You do not have permission to update variants.",
        "error",
        "Permissions Error"
      );
      return;
    }

    const newStatus = variant.status === "active" ? "inactive" : "active";
    try {
      setLoading(true);
      const response = await apiClient.post("/store-update-varient", {
        id: variant.id,
        status: newStatus,
        name: variant.name,
        value: JSON.parse(variant.value),
      });
      if (response.success) {
        showToast(
          `Variant status updated to ${newStatus}`,
          "success",
          "Variant Operation"
        );
        // Update the local state to reflect the change
        setVariants((prev) =>
          prev.map((v) =>
            v.id === variant.id ? { ...v, status: newStatus } : v
          )
        );
        setFilteredVariants((prev) =>
          prev.map((v) =>
            v.id === variant.id ? { ...v, status: newStatus } : v
          )
        );
      } else {
        showToast(
          response.message || "Failed to update variant status",
          "error",
          "Variant Operation"
        );
      }
    } catch (error) {
      showToast(
        error.message || "Failed to update variant status",
        "error",
        "Variant Operation"
      );
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: "Variant",
      dataIndex: "name",
      sorter: (a, b) => a.name.length - b.name.length,
      width: 200,
      render: (text) => <span className="wrap-text">{text}</span>,
    },

    {
      title: "Values",
      dataIndex: "value",
      sorter: (a, b) => {
        const aValue = Array.isArray(a.value) ? a.value : [];
        const bValue = Array.isArray(b.value) ? b.value : [];
        return aValue.length - bValue.length;
      },
      render: (value) => {
        let displayText = "";
        if (Array.isArray(value)) {
          displayText = value.join(", ");
        } else if (typeof value === "string") {
          try {
            const parsedValue = JSON.parse(value.replace(/'/g, '"'));
            displayText = Array.isArray(parsedValue)
              ? parsedValue.join(", ")
              : value;
          } catch (e) {
            displayText = value;
          }
        }
        return <span className="wrap-text">{displayText}</span>;
      },
      width: 200,
    },

    {
      title: "Created On",
      dataIndex: "created_at",
      sorter: (a, b) => a.created_at.length - b.created_at.length,
      render: (text) => {
        const date = new Date(text);
        return isNaN(date)
          ? "N/A"
          : date
              .toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
              })
              .split("/")
              .join("-");
      },
      width: 150,
    },

    // 👉 Show Status column only if user has read permission
    ...(permissions.read
      ? [
          {
            title: "Status",
            dataIndex: "status",
            render: (text, record) => (
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={text === "active"}
                  onChange={() => handleToggleStatus(record)}
                />
                <span className="toggle-slider"></span>
              </label>
            ),
            sorter: (a, b) => a.status.length - b.status.length,
            width: 100,
          },
        ]
      : []),

    // 👉 Show Actions column only if user has read OR delete permissions
    ...(permissions.read || permissions.delete
      ? [
          {
            title: "Actions",
            dataIndex: "actions",
            key: "actions",
            render: (_, record) => (
              <td className="action-table-data">
                <div className="edit-delete-action">
                  {permissions.update && ( // ✅ edit allowed only if update = true
                    <Link
                      className="me-2 p-2"
                      to="#"
                      data-bs-toggle="modal"
                      data-bs-target="#add-units"
                      onClick={() => handleEditVariants(record)}
                    >
                      <i data-feather="edit" className="feather-edit"></i>
                    </Link>
                  )}
                  {permissions.delete && ( // ✅ delete allowed only if delete = true
                    <Link
                      className="confirm-text p-2"
                      to="#"
                      onClick={() => showConfirmationAlert(record)}
                    >
                      <i data-feather="trash-2" className="feather-trash-2"></i>
                    </Link>
                  )}
                </div>
              </td>
            ),
            width: 100,
          },
        ]
      : []),
  ];

  const MySwal = withReactContent(Swal);

  const showConfirmationAlert = async (record) => {
    try {
      const result = await MySwal.fire({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        showCancelButton: true,
        confirmButtonColor: "#00ff00",
        confirmButtonText: "Yes, delete it!",
        cancelButtonColor: "#ff0000",
        cancelButtonText: "Cancel",
      });

      if (result.isConfirmed) {
        try {
          setLoading(true);
          await apiClient.post("/delete-varient", {
            id: record.id,
          });
          showToast("Variant deleted successfully", "success", "Variants");
          hasFetched.current = false;
          await fetchVariants(pagination.currentPage, pagination.pageSize);
        } catch (error) {
          showToast(
            error.message || "Failed to delete variant",
            "error",
            "Variants"
          );
        } finally {
          setLoading(false);
        }
      }
    } catch (error) {
      showToast("An error occurred", "error", "Variants");
    }
  };

  const { currentPage, pageSize, totalPages, totalCount } = pagination;
  const startItem = totalCount > 0 ? (currentPage - 1) * pageSize + 1 : 0;
  const endItem = Math.min(startItem + pageSize - 1, totalCount);

  const pagesToShow = [];
  if (totalPages > 0) {
    if (totalPages <= 2) {
      for (let i = 1; i <= totalPages; i++) {
        pagesToShow.push(i);
      }
    } else {
      let middle = [];
      if (currentPage === 1) {
        middle = [1, 2];
      } else if (currentPage === totalPages) {
        middle = [totalPages - 1, totalPages];
      } else {
        middle = [currentPage, currentPage + 1];
      }

      if (middle[0] > 1) {
        pagesToShow.push(1);
      }
      if (middle[0] > 2) {
        pagesToShow.push("...");
      }
      middle.forEach((p) => pagesToShow.push(p));
      if (middle[1] < totalPages - 1) {
        pagesToShow.push("...");
      }
      if (middle[1] < totalPages) {
        pagesToShow.push(totalPages);
      }
    }
  }

  const changePage = (page) => {
    setPagination((prev) => ({
      ...prev,
      currentPage: page,
    }));
    hasFetched.current = false;
  };

  return (
    <>
      <Loader loading={loading} />
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="add-item d-flex">
              <div className="page-title">
                <h4>Variant Attributes</h4>
                <h6>Manage your variant attributes</h6>
              </div>
            </div>
            <ul className="table-top-head">
              <li>
                <OverlayTrigger placement="top" overlay={renderCollapseTooltip}>
                  <Link
                    data-bs-toggle="tooltip"
                    data-bs-placement="top"
                    id="collapse-header"
                    className={data ? "active" : ""}
                    onClick={() => {
                      dispatch(setToogleHeader(!data));
                    }}
                  >
                    <ChevronUp />
                  </Link>
                </OverlayTrigger>
              </li>
            </ul>
            <div className="page-btn">
              {permissions.create && (
                <a
                  to="#"
                  className="btn btn-added"
                  data-bs-toggle="modal"
                  data-bs-target="#add-units"
                  onClick={handleAddVariants}
                >
                  <PlusCircle className="me-2" />
                  Add New Variant
                </a>
              )}
            </div>
          </div>
          {/* /product list */}
          <div className="card table-list-card">
            <div className="card-body">
              <div className="table-top">
                <div className="search-set">
                  <div className="search-input">
                    <input
                      type="text"
                      placeholder="Search by variant and values"
                      className="form-control form-control-sm formsearch"
                      onChange={handleSearch}
                    />
                    <Link to className="btn btn-searchset">
                      <i data-feather="search" className="feather-search" />
                    </Link>
                  </div>
                </div>
              </div>
              <div className="table-responsive">
                <Table
                  columns={columns}
                  dataSource={filteredVariants}
                  rowKey={(record) => record.id}
                  rowSelection={null}
                  pagination={false}
                />
              </div>

              {totalCount > 0 && (
                <div className="custom-pagination d-flex justify-content-between align-items-center mt-4 mb-4">
                  <div className="pagination-left">
                    Showing {startItem} - {endItem} of {totalCount}
                  </div>
                  <div className="pagination-right d-flex align-items-center">
                    <button
                      className="btn btn-sm btn-outline-secondary me-2"
                      disabled={currentPage === 1}
                      onClick={() => changePage(currentPage - 1)}
                    >
                      <LeftOutlined size={12} />
                    </button>
                    {pagesToShow.map((page, index) => (
                      <span key={index} className="me-2">
                        {page === "..." ? (
                          <span className="pagination-ellipsis">...</span>
                        ) : (
                          <button
                            className={`btn btn-sm ${
                              page === currentPage
                                ? "btn-primary"
                                : "btn-outline-secondary"
                            }`}
                            onClick={() => changePage(page)}
                          >
                            {page}
                          </button>
                        )}
                      </span>
                    ))}
                    <button
                      className="btn btn-sm btn-outline-secondary ms-2"
                      disabled={currentPage === totalPages}
                      onClick={() => changePage(currentPage + 1)}
                    >
                      <RightOutlined size={12} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
          {/* /product list */}
        </div>
        <AddVariant
          variant={currentVariant}
          onSave={() => {
            hasFetched.current = false;
            fetchVariants(pagination.currentPage, pagination.pageSize);
          }}
        />
      </div>
    </>
  );
};

export default VariantAttributes;
